import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import { withStyles } from '@material-ui/core/styles';
import {
  Grid, Paper, Divider, Button, FormControlLabel, CircularProgress,
} from '@material-ui/core';
import { KeyboardDateTimePicker } from '@material-ui/pickers';
import { doFetch } from '@trdk/style/Redux/fetch.actions';
import * as Yup from 'yup';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import ListItemText from '@material-ui/core/ListItemText';
import Select from '@material-ui/core/Select';
import Checkbox from '@material-ui/core/Checkbox';
import Chip from '@material-ui/core/Chip';
import {
  getElementBySysId, isElementInList, getSysIdListFromObjectList,
  getLocationName, getWorkPlanFromAssignmentList, dateTimeToString,
} from '../utils';
import TitleField from '../components/title-field';
import { createOrUpdateControl } from '../services/submit/submit-servce';
import NotificationBox from '../components/notification-box';
import { fetchAssignments } from '../services/fetch/load-form-service';

const validationSchema = Yup.object().shape({
  startTime: Yup.date().required('Feltet er påkrevd'),
  controllers: Yup.array().required('Må ha minst én kontrollør'),
});

class NewControl extends Component {
  constructor(props) {
    super(props);
    this.state = {
      submitting: false,
      submitted: false,
      submitError: false,
    };
  }

  render() {
    const { submitting, submitted, submitError } = this.state;

    const {
      classes, dispatch, history, match: { params: { licenseID } },
      licenses: { payload: licensesPayload },
      assignments: { payload: assignmentsPayload },
      controllers: { payload: controllersPayload },
      activeWorkPlan,
    } = this.props;

    const workPlanId = activeWorkPlan ? activeWorkPlan.sysId : '';
    const license = getElementBySysId(licensesPayload, licenseID);
    if (license == null) {
      return (
        <div>
          {`Ingen bevilling funnet med sysId: ${licenseID}`}
        </div>
      );
    }

    if (submitting && !submitted) {
      return (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      );
    }

    const workPlan = getWorkPlanFromAssignmentList(assignmentsPayload, workPlanId);

    // TODO use authenticated user as default controller if no work plan selected
    const defaultControllers = [];
    if (workPlan && workPlan.controllers) {
      controllersPayload.forEach((c) => {
        if (isElementInList(workPlan.controllers, c.controllerSysId, 'controllerSysId')) {
          defaultControllers.push(c);
        }
      });
    }

    return (
      <div>
        <TitleField title="Ny kontroll" subtitle={getLocationName(license)} />
        <Formik
          initialValues={{
            startNow: true,
            startTime: new Date(),
            controllers: defaultControllers,
          }}
          onSubmit={(v) => {
            this.setState({ submitted: false });
            this.setState({ submitting: true });

            const payload = {
              license: licenseID,
              startTime: v.startNow ? new Date() : v.startTime,
              workplan: workPlanId,
              controllers: getSysIdListFromObjectList(v.controllers, 'controllerSysId'),
            };
            payload.startTime = dateTimeToString(payload.startTime, false);

            createOrUpdateControl(payload, 'POST')
              .then((res) => {
                this.setState({ submitting: false });
                this.setState({ submitted: true });
                if (!res.ok) {
                  this.setState({ submitError: true });
                  throw res;
                }
                return res.json();
              })
              .then((responseBody) => {
                const path = history.location.pathname.split('/');
                const s = path.slice(0, path.length - 1).join('/');
                history.replace(`${s}/kontroll--${responseBody.result.sys_id}`);

                dispatch(doFetch('ASSIGNMENTS', fetchAssignments()
                  .then((assignmentsRes) => {
                    if (!assignmentsRes.ok) { throw assignmentsRes; }
                    return assignmentsRes.json();
                  })));
              });
          }}
          validationSchema={validationSchema}
        >
          {({
            values, errors, touched, handleBlur, setFieldValue,
          }) => (
            <Form>
              <Paper className={classes.paper} elevation={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl className={classes.formControl}>
                      <KeyboardDateTimePicker
                        ampm={false}
                        name="startTime"
                        value={values.startTime}
                        error={!!(errors.startTime && touched.startTime)}
                        label="Start-tid"
                        disabled={values.startNow}
                        disableFuture
                        showTodayButton
                        todayLabel="I dag"
                        cancelLabel="Avbryt"
                        format="dd.MM.yyyy HH:mm"
                        invalidDateMessage="Ugyldig dato"
                        onChange={(e) => {
                          setFieldValue('startTime', e);
                        }}
                        onBlur={(e) => {
                          handleBlur(e);
                        }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      className={classes.formControl}
                      control={(
                        <Checkbox
                          checked={values.startNow}
                          value={values.startNow}
                          name="startNow"
                          color="primary"
                        />
                      )}
                      onChange={(e) => {
                        setFieldValue('startTime', new Date());
                        setFieldValue('startNow', e.target.checked);
                      }}
                      onBlur={handleBlur}
                      label="Start nå"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl className={classes.formControl}>
                      <InputLabel>Kontrollører</InputLabel>
                      <Select
                        error={!!(errors.controllers && touched.controllers)}
                        multiple
                        value={values.controllers}
                        onChange={(e) => {
                          setFieldValue('controllers', e.target.value);
                        }}
                        input={<Input />}
                        renderValue={selected => (
                          <div className={classes.chips}>
                            {selected.map(value => (
                              <Chip
                                key={value.controllerSysId}
                                label={`${value.firstName} ${value.lastName} (${value.controllerNumber})`}
                                className={classes.chip}
                              />
                            ))}
                          </div>
                        )}
                      >
                        {controllersPayload.map(controller => (
                          <MenuItem
                            key={controller.controllerSysId}
                            value={controller}
                          >
                            <Checkbox
                              color="primary"
                              checked={isElementInList(
                                values.controllers, controller.controllerSysId, 'controllerSysId',
                              )}
                            />
                            <ListItemText
                              primary={`${controller.firstName} ${controller.lastName} (${controller.controllerNumber})`}
                            />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}><Divider /></Grid>
                  <Grid item xs={12}>
                    <Button color="primary" type="submit" variant="contained" fullWidth>
                      Opprett kontroll
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Form>
          )}
        </Formik>
        {submitted && submitError && (
          <NotificationBox variant="error" header="Feil oppsto under lagring av endringer" showIcon margin="2rem 0" />
        )}
      </div>
    );
  }
}

const styles = theme => ({
  paper: {
    padding: '0.8rem',
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: '10rem',
    maxWidth: '100%',
  },
  chip: {
    marginRight: theme.spacing(1),
  },
  loading: {
    margin: theme.spacing(5),
    textAlign: 'center',
  },
});

NewControl.propTypes = {
  match: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  licenses: PropTypes.object.isRequired,
  assignments: PropTypes.object.isRequired,
  controllers: PropTypes.object.isRequired,
  activeWorkPlan: PropTypes.object,
};

NewControl.defaultProps = {
  activeWorkPlan: null,
};

const mapStateToProps = ({
  controllers,
  licenses,
  assignments,
  form: {
    activeWorkPlan,
  },
}) => ({
  controllers,
  licenses,
  assignments,
  activeWorkPlan,
});

export default connect(mapStateToProps)(withRouter(withStyles(styles)(NewControl)));
